import type { MaybeRefOrGetter } from "vue";
import type { DebugApp, DebugConfigAdapter, DebugSection } from "./types";
import { useDialogStack } from "@verbleif/lib/src/composables/Dialog/useDialogStack";
import { useEventListener } from "@vueuse/core";
import { toValue } from "vue";
import DebugToolsDialog from "./DebugToolsDialog.vue";

export const DEBUG_TOOLS_DIALOG_ID = "debug-tools";

export interface UseDebugToolsOptions {
  app: DebugApp
  config: DebugConfigAdapter
  sections: DebugSection[]
  /** Shows the Beheerder label in the header. The login app has no user, so it leaves this out. */
  admin?: MaybeRefOrGetter<boolean>
}

let registration: UseDebugToolsOptions | null = null;

function isShortcut(event: KeyboardEvent): boolean {
  return event.ctrlKey && event.shiftKey && !event.altKey && !event.metaKey && event.key.toLowerCase() === "d";
}

/**
 * Opens the debug tools of this app, or returns the open one: there is only ever one. `initialSection` picks the
 * tab it opens on. Throws when the app did not register them with `useDebugTools`.
 */
export function openDebugTools(initialSection?: string): string {
  if (registration === null) {
    throw new Error("The debug tools are not registered: call useDebugTools() in App.vue first.");
  }
  const options = registration;
  const stack = useDialogStack();
  const existing = stack.getById(DEBUG_TOOLS_DIALOG_ID);
  if (existing) {
    return existing.instanceId;
  }

  return stack.push({
    id: DEBUG_TOOLS_DIALOG_ID,
    component: DebugToolsDialog,
    size: "xlarge",
    height: "tall",
    headless: true,
    paddingless: true,
    props: {
      app: options.app,
      sections: options.sections,
      config: options.config,
      initialSection,
      admin: () => toValue(options.admin) ?? false,
    },
  });
}

/**
 * Registers the debug tools of an app (call once, in App.vue): the sections it shows, the config adapter of its
 * config store, and Ctrl+Shift+D to open them.
 */
export function useDebugTools(options: UseDebugToolsOptions) {
  registration = options;

  useEventListener(window, "keydown", (event: KeyboardEvent) => {
    if (!isShortcut(event)) {
      return;
    }
    event.preventDefault();
    openDebugTools();
  });

  return {
    open: openDebugTools,
  };
}

/** Forgets the registration; for tests. */
export function resetDebugToolsRegistration(): void {
  registration = null;
}
